import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Button,
  Modal,
  Alert,
  Spinner
} from "react-bootstrap";
import {
  FaClock,
  FaUsers,
  FaSearch,
  FaInfoCircle,
  FaGraduationCap,
  FaBookOpen,
  FaStar,
  FaFilter,
  FaTimes,
  FaChevronRight,
  FaPlayCircle,
  FaWhatsapp
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { BASE_URL } from "../../services/api";

// COURSE IMAGES
const COURSE_IMAGES = {
  nursing: "https://images.unsplash.com/photo-1584515933487-779824d29309?auto=format&fit=crop&w=1200&q=80",
  bpharma: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?auto=format&fit=crop&w=1200&q=80",
  dpharma: "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=1200&q=80",
  allied: "https://images.unsplash.com/photo-1579154204601-01588f351e67?auto=format&fit=crop&w=1200&q=80",
  midwifery: "https://images.unsplash.com/photo-1588776814546-ec7e44d3f99c?auto=format&fit=crop&w=1200&q=80",
  medical: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=1200&q=80",
  mlt: "https://images.unsplash.com/photo-1579154204601-01588f351e67?auto=format&fit=crop&w=1200&q=80",
  imaging: "https://images.unsplash.com/photo-1581595219315-a187dbe40178?auto=format&fit=crop&w=1200&q=80",
  anesthesia: "https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&w=1200&q=80",
  dialysis: "https://images.unsplash.com/photo-1581595219315-a187dbe40178?auto=format&fit=crop&w=1200&q=80",
  default: "https://images.unsplash.com/photo-1584516150909-c43483ee7938?auto=format&fit=crop&w=1200&q=80"
};

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100
    }
  }
};

const heroVariants = {
  hidden: { opacity: 0, y: -50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: "easeOut"
    }
  }
};

const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hoveredCard, setHoveredCard] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [enrolling, setEnrolling] = useState(null);

  const searchRef = useRef(null);

  // WhatsApp number (replace with your actual number)
  const WHATSAPP_NUMBER = "+919916197799";

  // GET IMAGE BASED ON COURSE NAME
  const getCourseImage = useCallback((name) => {
    const text = name.toLowerCase();

    if (text.includes("gnm") || text.includes("nursing")) return COURSE_IMAGES.nursing;
    if (text.includes("b") && text.includes("pharm")) return COURSE_IMAGES.bpharma;
    if (text.includes("d") && text.includes("pharm")) return COURSE_IMAGES.dpharma;
    if (text.includes("midwifery")) return COURSE_IMAGES.midwifery;
    if (text.includes("medical")) return COURSE_IMAGES.medical;
    
    // Allied Health Sciences specific courses
    if (text.includes("medical laboratory") || text.includes("mlt")) return COURSE_IMAGES.mlt;
    if (text.includes("imaging") || text.includes("radiology")) return COURSE_IMAGES.imaging;
    if (text.includes("anesthesia") || text.includes("operation theatre") || text.includes("ott")) return COURSE_IMAGES.anesthesia;
    if (text.includes("dialysis")) return COURSE_IMAGES.dialysis;
    
    // Generic allied health
    if (text.includes("allied") || text.includes("health science") || text.includes("paramedical")) return COURSE_IMAGES.allied;

    return COURSE_IMAGES.default;
  }, []);

  // GET COURSE CATEGORY
  const getCourseCategory = useCallback((name) => {
    const text = name.toLowerCase();
    
    if (text.includes("nursing")) return "nursing";
    if (text.includes("pharm")) return "pharmacy";
    
    // Allied Health Sciences categories
    if (text.includes("medical laboratory") || text.includes("mlt")) return "allied-mlt";
    if (text.includes("imaging") || text.includes("radiology")) return "allied-imaging";
    if (text.includes("anesthesia") || text.includes("operation theatre") || text.includes("ott")) return "allied-anesthesia";
    if (text.includes("dialysis")) return "allied-dialysis";
    
    // Generic allied health
    if (text.includes("allied") || text.includes("paramedical") || text.includes("health science")) return "allied";
    
    if (text.includes("medical")) return "medical";
    return "other";
  }, []);

  // FETCH COURSES
  const fetchCourses = async () => {
    try {
      const res = await fetch(`${BASE_URL}/courses.php`);
      const data = await res.json();
      if (data.success) setCourses(data.data);
      else setError("No courses available");
    } catch {
      setError("Failed to load courses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  // Filter courses
  const filteredCourses = courses.filter((c) => {
    const matchesSearch = c.name.toLowerCase().includes(search.toLowerCase()) ||
                         c.description?.toLowerCase().includes(search.toLowerCase());
    
    let matchesCategory = true;
    
    if (selectedCategory === "all") {
      matchesCategory = true;
    } else if (selectedCategory === "allied") {
      // Allied Health Sciences includes all allied courses
      matchesCategory = getCourseCategory(c.name).startsWith("allied");
    } else {
      matchesCategory = getCourseCategory(c.name) === selectedCategory;
    }
    
    return matchesSearch && matchesCategory;
  });

  // Categories
  const categories = [
    { id: "all", label: "All Courses", icon: <FaGraduationCap /> },
    { id: "nursing", label: "Nursing", icon: <FaStar /> },
    { id: "pharmacy", label: "Pharmacy", icon: <FaBookOpen /> },
    { 
      id: "allied", 
      label: "Allied Health Sciences", 
      icon: <FaUsers />,
      subcategories: [
        { id: "allied-mlt", label: "B.Sc. Medical Laboratory Technology" },
        { id: "allied-imaging", label: "B.Sc. Imaging Technology" },
        { id: "allied-anesthesia", label: "B.Sc. Anesthesia & Operation Theatre Technology" },
        { id: "allied-dialysis", label: "B.Sc. Dialysis Technology" }
      ]
    }
  ];

  const handleEnrollClick = (courseId, courseName) => {
    setEnrolling(courseId);
    
    // Create WhatsApp message
    const message = encodeURIComponent(
      `Hello! I'm interested in enrolling for the ${courseName} course. Could you please provide more details about the admission process, fees, and eligibility criteria?`
    );
    
    // Open WhatsApp
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
    
    setTimeout(() => setEnrolling(null), 2000);
  };

  const handleWhatsAppEnquiry = (courseName) => {
    const message = encodeURIComponent(
      `Hi! I would like to get more information about the ${courseName} course. Please share the details regarding: \n\n` +
      `• Admission process\n` +
      `• Course fees\n` +
      `• Eligibility criteria\n` +
      `• Duration\n` +
      `• Career opportunities\n\n` +
      `Thank you!`
    );
    
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Spinner animation="border" variant="primary" className="mb-3" />
          <h4>Loading Amazing Courses...</h4>
          <p className="text-muted">Preparing your educational journey</p>
        </motion.div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Alert variant="warning" className="text-center">
            <Alert.Heading>Oops! Something went wrong</Alert.Heading>
            <p>{error}</p>
            <Button variant="outline-primary" onClick={fetchCourses}>
              Try Again
            </Button>
          </Alert>
        </motion.div>
      </Container>
    );
  }

  return (
    <div className="courses-page">
      {/* HERO SECTION */}
      <motion.div 
        className="courses-hero"
        initial="hidden"
        animate="visible"
        variants={heroVariants}
      >
        <Container>
          <Row className="align-items-center">
            <Col lg={6}>
              <motion.h1
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
              >
                Explore Our Courses
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                Discover programs in Nursing, Pharmacy, and Allied Health Sciences including 
                B.Sc. Medical Laboratory Technology, B.Sc. Imaging Technology, 
                B.Sc. Anesthesia & Operation Theatre Technology, and B.Sc. Dialysis Technology.
                Your journey to excellence starts here.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.6 }}
              >
                <Button 
                  variant="light" 
                  size="lg"
                  onClick={() => searchRef.current?.focus()}
                  className="me-3"
                >
                  <FaSearch className="me-2" />
                  Find Your Course
                </Button>
                <Button 
                  variant="outline-light" 
                  size="lg"
                  onClick={() => handleWhatsAppEnquiry("all courses")}
                >
                  <FaWhatsapp className="me-2" />
                  Enquire on WhatsApp
                </Button>
              </motion.div>
            </Col>
            <Col lg={6}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4, duration: 0.8 }}
              >
                <img 
                  className="hero-img" 
                  src="https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=1200&q=80" 
                  alt="courses hero"
                />
              </motion.div>
            </Col>
          </Row>
        </Container>
      </motion.div>

      <Container className="py-5">
        {/* SEARCH AND FILTERS */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <div className="search-section">
            <div className="search-box" ref={searchRef}>
              <FaSearch className="search-icon" />
              <input
                placeholder="Search by course name or description..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <motion.button
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="clear-search"
                  onClick={() => setSearch("")}
                >
                  <FaTimes />
                </motion.button>
              )}
            </div>

            <motion.button
              className={`filter-toggle ${showFilters ? "active" : ""}`}
              onClick={() => setShowFilters(!showFilters)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaFilter /> Filters
            </motion.button>
          </div>

          <AnimatePresence>
            {showFilters && (
              <motion.div
                className="categories-container"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="categories">
                  {categories.map((category) => (
                    <div key={category.id} className="category-group">
                      <motion.button
                        className={`category-btn ${selectedCategory === category.id || 
                          (category.subcategories && category.subcategories.some(sub => selectedCategory === sub.id)) 
                          ? "active" : ""}`}
                        onClick={() => setSelectedCategory(category.id)}
                        whileHover={{ scale: 1.05, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <span className="category-icon">{category.icon}</span>
                        {category.label}
                      </motion.button>
                      
                      {category.subcategories && (
                        <AnimatePresence>
                          {(selectedCategory === category.id || 
                            category.subcategories.some(sub => selectedCategory === sub.id)) && (
                            <motion.div
                              className="subcategories"
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                            >
                              {category.subcategories.map((sub) => (
                                <motion.button
                                  key={sub.id}
                                  className={`subcategory-btn ${selectedCategory === sub.id ? "active" : ""}`}
                                  onClick={() => setSelectedCategory(sub.id)}
                                  whileHover={{ x: 5 }}
                                  whileTap={{ scale: 0.95 }}
                                >
                                  {sub.label}
                                  {selectedCategory === sub.id && (
                                    <motion.span
                                      className="active-indicator"
                                      layoutId="activeSubcategory"
                                    />
                                  )}
                                </motion.button>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* COURSES GRID */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Row className="g-4">
            <AnimatePresence>
              {filteredCourses.map((course) => (
                <Col lg={4} md={6} key={course.id}>
                  <motion.div
                    variants={itemVariants}
                    layout
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    whileHover={{ 
                      y: -10,
                      transition: { duration: 0.3 }
                    }}
                    onHoverStart={() => setHoveredCard(course.id)}
                    onHoverEnd={() => setHoveredCard(null)}
                  >
                    <Card className="course-card">
                      <div className="course-img-wrapper">
                        <div 
                          className="course-img"
                          style={{ 
                            backgroundImage: `url(${getCourseImage(course.name)})`,
                            transform: hoveredCard === course.id ? 'scale(1.1)' : 'scale(1)'
                          }}
                        />
                        <div className="course-overlay">
                          <Badge bg="primary" className="course-badge">
                            {course.code}
                          </Badge>
                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ 
                              opacity: hoveredCard === course.id ? 1 : 0,
                              y: hoveredCard === course.id ? 0 : 20
                            }}
                            className="quick-actions"
                          >
                            <Button 
                              variant="light" 
                              size="sm"
                              onClick={() => {
                                setSelectedCourse(course);
                                setShowModal(true);
                              }}
                            >
                              Quick View
                            </Button>
                          </motion.div>
                        </div>
                      </div>
                      
                      <Card.Body>
                        <h4>{course.name}</h4>
                        
                        <motion.div
                          animate={{ height: hoveredCard === course.id ? 'auto' : '3em' }}
                          className="description-container"
                        >
                          <p className="course-description">
                            {course.description || "No description available"}
                          </p>
                        </motion.div>

                        <div className="course-meta">
                          <motion.span
                            whileHover={{ scale: 1.1 }}
                            className="meta-item"
                          >
                            <FaClock /> {course.duration || "N/A"}
                          </motion.span>
                          <motion.span
                            whileHover={{ scale: 1.1 }}
                            className="meta-item"
                          >
                            <FaUsers /> {course.intake_seats || "N/A"} Seats
                          </motion.span>
                        </div>

                        <div className="course-actions">
                          <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="action-btn-wrapper"
                          >
                            <Button
                              variant="primary"
                              className="details-btn"
                              onClick={() => {
                                setSelectedCourse(course);
                                setShowModal(true);
                              }}
                            >
                              View Details
                              <FaChevronRight className="ms-2" />
                            </Button>
                          </motion.div>

                          <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="action-btn-wrapper"
                          >
                            <Button
                              variant="success"
                              className="whatsapp-btn"
                              onClick={() => handleEnrollClick(course.id, course.name)}
                              disabled={enrolling === course.id}
                            >
                              {enrolling === course.id ? (
                                <>
                                  <Spinner size="sm" /> Opening...
                                </>
                              ) : (
                                <>
                                  <FaWhatsapp className="me-2" />
                                  Enquire
                                </>
                              )}
                            </Button>
                          </motion.div>
                        </div>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </AnimatePresence>
          </Row>
        </motion.div>

        {/* NO RESULTS */}
        {filteredCourses.length === 0 && (
          <motion.div
            className="no-results"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h4>No courses found</h4>
            <p>Try adjusting your search or filters</p>
            <Button variant="primary" onClick={() => {
              setSearch("");
              setSelectedCategory("all");
            }}>
              Clear Filters
            </Button>
          </motion.div>
        )}
      </Container>

      {/* DETAIL MODAL */}
      <AnimatePresence>
        {selectedCourse && (
          <Modal
            show={showModal}
            onHide={() => setShowModal(false)}
            size="lg"
            centered
            className="course-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              transition={{ duration: 0.3 }}
            >
              <Modal.Header closeButton>
                <Modal.Title>{selectedCourse.name}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <motion.img
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="modal-img"
                  src={getCourseImage(selectedCourse.name)}
                  alt={selectedCourse.name}
                />
                
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <h5 className="mt-4">
                    <FaInfoCircle className="me-2" />
                    Course Overview
                  </h5>
                  <p>{selectedCourse.description}</p>
                </motion.div>

                <motion.div
                  className="course-meta mt-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <span><FaClock /> Duration: {selectedCourse.duration}</span>
                  <span><FaUsers /> Seats: {selectedCourse.intake_seats}</span>
                </motion.div>

                <motion.div
                  className="modal-whatsapp"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <Button
                    variant="success"
                    size="lg"
                    className="w-100 mt-3"
                    onClick={() => handleWhatsAppEnquiry(selectedCourse.name)}
                  >
                    <FaWhatsapp className="me-2" />
                    Enquire on WhatsApp
                  </Button>
                </motion.div>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                  Close
                </Button>
                <Button 
                  variant="primary"
                  onClick={() => {
                    setShowModal(false);
                    handleEnrollClick(selectedCourse.id, selectedCourse.name);
                  }}
                >
                  Enroll Now
                </Button>
              </Modal.Footer>
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>

      <style>{`
        .courses-page {
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
          min-height: 100vh;
        }

        .courses-hero {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 100px 0;
          position: relative;
          overflow: hidden;
        }

        .courses-hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320"><path fill="%23ffffff" fill-opacity="0.1" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,154.7C960,171,1056,181,1152,165.3C1248,149,1344,107,1392,85.3L1440,64L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path></svg>');
          background-repeat: no-repeat;
          background-position: bottom;
          opacity: 0.3;
        }

        .hero-img {
          width: 100%;
          border-radius: 20px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.2);
        }

        .search-section {
          display: flex;
          gap: 15px;
          margin-bottom: 20px;
          align-items: center;
        }

        .search-box {
          flex: 1;
          display: flex;
          align-items: center;
          background: white;
          padding: 15px 20px;
          border-radius: 50px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
          position: relative;
        }

        .search-icon {
          color: #667eea;
          margin-right: 10px;
        }

        .search-box input {
          border: none;
          outline: none;
          width: 100%;
          font-size: 16px;
        }

        .clear-search {
          position: absolute;
          right: 15px;
          background: none;
          border: none;
          color: #999;
          cursor: pointer;
          padding: 5px;
        }

        .filter-toggle {
          padding: 15px 25px;
          background: white;
          border: none;
          border-radius: 50px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
          font-weight: 500;
          color: #667eea;
          cursor: pointer;
          transition: all 0.3s;
        }

        .filter-toggle.active {
          background: #667eea;
          color: white;
        }

        .categories-container {
          margin-bottom: 30px;
        }

        .categories {
          display: flex;
          gap: 20px;
          flex-wrap: wrap;
        }

        .category-group {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .category-btn {
          padding: 12px 24px;
          background: white;
          border: none;
          border-radius: 30px;
          font-weight: 500;
          color: #333;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          gap: 8px;
          box-shadow: 0 4px 15px rgba(0,0,0,0.1);
        }

        .category-btn.active {
          background: #667eea;
          color: white;
        }

        .category-icon {
          font-size: 16px;
        }

        .subcategories {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-left: 20px;
          padding-left: 15px;
          border-left: 2px solid #667eea;
        }

        .subcategory-btn {
          padding: 8px 16px;
          background: rgba(255,255,255,0.9);
          border: none;
          border-radius: 20px;
          font-size: 14px;
          color: #555;
          cursor: pointer;
          transition: all 0.3s;
          text-align: left;
          position: relative;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .subcategory-btn.active {
          background: #667eea;
          color: white;
        }

        .active-indicator {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: #ff6b6b;
        }

        .course-card {
          border: none;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 15px 35px rgba(0,0,0,0.1);
          transition: all 0.3s;
          background: rgba(255,255,255,0.9);
          backdrop-filter: blur(10px);
        }

        .course-img-wrapper {
          position: relative;
          overflow: hidden;
          height: 200px;
        }

        .course-img {
          height: 100%;
          background-size: cover;
          background-position: center;
          transition: transform 0.5s;
        }

        .course-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(to bottom, transparent 50%, rgba(0,0,0,0.7));
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 15px;
        }

        .course-badge {
          font-size: 12px;
          padding: 5px 10px;
        }

        .quick-actions {
          position: absolute;
          bottom: 15px;
          left: 0;
          right: 0;
          display: flex;
          justify-content: center;
        }

        .description-container {
          overflow: hidden;
          transition: height 0.3s;
          margin: 10px 0;
        }

        .course-description {
          color: #666;
          line-height: 1.6;
          font-size: 14px;
        }

        .course-meta {
          display: flex;
          gap: 20px;
          margin: 15px 0;
          padding: 10px 0;
          border-top: 1px solid #eee;
          border-bottom: 1px solid #eee;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 5px;
          color: #555;
          font-size: 13px;
          cursor: default;
        }

        .course-actions {
          display: flex;
          gap: 10px;
        }

        .action-btn-wrapper {
          flex: 1;
        }

        .details-btn, .whatsapp-btn {
          width: 100%;
          border-radius: 30px;
          padding: 8px 15px;
          font-size: 14px;
        }

        .whatsapp-btn {
          background: #25D366;
          border-color: #25D366;
        }

        .whatsapp-btn:hover {
          background: #128C7E;
          border-color: #128C7E;
        }

        .no-results {
          text-align: center;
          padding: 60px;
          background: white;
          border-radius: 20px;
          box-shadow: 0 15px 35px rgba(0,0,0,0.1);
        }

        .modal-img {
          width: 100%;
          height: 300px;
          object-fit: cover;
          border-radius: 15px;
        }

        .modal-whatsapp {
          margin-top: 20px;
        }

        .course-modal .modal-content {
          border-radius: 20px;
          overflow: hidden;
        }

        .course-modal .modal-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .course-modal .btn-close {
          filter: brightness(0) invert(1);
        }

        @media (max-width: 768px) {
          .courses-hero {
            padding: 60px 0;
          }
          
          .course-actions {
            flex-direction: column;
          }
          
          .categories {
            flex-direction: column;
          }
          
          .subcategories {
            margin-left: 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default Courses;